import Vue from 'vue';
import { router } from '@inertiajs/vue2';
import { createInertiaApp } from '@inertiajs/vue2';
import { useModal } from '@/mixins/use-modal';
import vuetify from '@/plugins/vuetify';
import i18n from '@/plugins/vue-i18n';
import PortalVue from 'portal-vue';
import { storageAvailable } from '@/lib/utils';
import axios from 'axios';

axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const LOCAL_STORAGE_AVAILABLE = storageAvailable('localStorage');

Vue.use(PortalVue);

require('@/lib/register-components');

Vue.mixin({
  methods: {
    route: window.route,
    isRoute(...routes) {
      return routes.some(route => this.route().current(route));
    }
  }
});

window.eventBus = new Vue;

createInertiaApp({
  progress: {
    color: '#29d',
  },
  resolve: name => require(`./Pages/${name}`),
  setup({ el, App, props, plugin }) {
    Vue.use(plugin)

    new Vue({
      vuetify,
      i18n,

      mixins: [ useModal ],

      data() {
        return {
          notificationsDialog: {
            show: false
          },
          sidebar: {
            open: null
          },
          preview: {
            faq: {
              show: null,
              key: 0,
              component: 'embed',
              language: null
            }
          }
        }
      },

      computed: {
        sidebarItems() {
          const hasUnseenNotifications = this.$page.props.user.notifications.some(
            notification => ! notification.seen
          );

          return [
            {
              highlightRoutes: ['dashboard'],
              url: this.route('dashboard'),
              icon: 'mdi-view-dashboard',
              title: i18n.t('Dashboard'),
              disabled: false
            },
            {
              highlightRoutes: ['faq.index'],
              url: this.route('faq.index'),
              icon: 'mdi-frequently-asked-questions',
              title: i18n.t('FAQs'),
              disabled: false
            },
            {
              highlightRoutes: ['media.index'],
              url: this.route('media.index'),
              icon: 'mdi-file-multiple',
              title: i18n.t('Media library'),
              disabled: false
            },
            {
              type: 'expansion-list',
              open: false,
              disabled: false,
              icon: 'mdi-cog',
              title: i18n.t('Settings'),
              items: [
                {
                  highlightRoutes: ['account.index'],
                  disabled: false,
                  url: this.route('account.index'),
                  title: i18n.t('Account')
                },
                {
                  show: this.$page.props.user.current_team.owned_by_user,
                  disabled: !this.$page.props.user.verified,
                  url: '/billing',
                  inertiaLink: false,
                  title: i18n.t('Billing')
                },
                {
                  highlightRoutes: ['team.show', 'team.create'],
                  disabled: false,
                  url: this.route('team.show', this.$page.props.user.current_team.id),
                  title: i18n.t('Team management')
                }
              ]
            },
            {
              url: null,
              onClick: () => {
                if (this.$vuetify.breakpoint.mobile) {
                  this.sidebar.open = false;
                }
                this.showNotificationsDialog();
              },
              icon: hasUnseenNotifications ? 'mdi-bell-ring-outline' : 'mdi-bell-outline',
              iconHasBadge: () => hasUnseenNotifications,
              title: i18n.t('Notifications'),
              disabled: false,
            },
            {
              show: true,
              disabled: false,
              url: null,
              onClick: () => {
                this.showSupportDialog();
              },
              icon: 'mdi-help-circle',
              title: i18n.t('Support')
            },
            {
              show: true,
              disabled: false,
              url: null,
              onClick: () => {
                window.open('/api-docs');
              },
              icon: 'mdi-api',
              title: i18n.t('API documentation')
            }
          ]
        }
      },

      watch: {
        'preview.faq.show': function(value) {
          if (LOCAL_STORAGE_AVAILABLE) {
            window.localStorage.setItem('webcomet-live-preview', value);
          }

          window.eventBus.$emit('faq.settings-layout:toggle-live-preview');
        }
      },

      created() {
        router.on('navigate', () => {
          if (this.$vuetify.breakpoint.mobile) {
            this.sidebar.open = false;
          }

          this.$i18n.locale = this.$page.props.app_locales.current.code;
        });

        if (this.$vuetify.breakpoint.smAndDown) {
          this.preview.faq.show = false;
        }

        const livePreviewEnabled = window.localStorage.getItem('webcomet-live-preview');
        if (LOCAL_STORAGE_AVAILABLE && livePreviewEnabled !== null) {
          this.preview.faq.show = livePreviewEnabled === 'true';
        } else {
          this.preview.faq.show = true;
        }
      },

      methods: {
        navigateToItem(item) {
          if (this.isActiveUrl(item)) {
            return;
          }

          if (! item.url && item.onClick) {
            return item.onClick();
          }

          if (item.inertiaLink === false) {
            window.location.href = item.url.toString();
          } else {
            this.$inertia.visit(item.url);
          }
         },

        isActiveUrl(item) {
          if (item.highlightRoutes) {
            return item.highlightRoutes.includes(
              this.route().current()
            );
          }

          return false;
        },

        showSupportDialog() {
          window.open(this.$page.props.support_url);
        },

        showNotificationsDialog() {
          this.notificationsDialog.show = true;
        },

        hideNotificationsDialog() {
          this.notificationsDialog.show = false;
        },

        refreshFaqPreview() {
          this.preview.faq.key++;
        }
      },

      render: h => h(App, props),
    }).$mount(el)
  },
})